/**
 * Administration config screens (TASK-68) — shared types.
 *
 * Covers the 7 HotelKey-parity config surfaces: Charge Types, Promotion Codes,
 * Cancellation Reason Codes, Deposit Policies, Rate Plan Packages (header +
 * elements), Housekeeping Service Types, and Online Check-Out settings.
 *
 * These response interfaces mirror the API-serialized entity shape: camelCase
 * field names, money/DECIMAL fields as `string` (read with parseFloat), ids and
 * timestamps as `string`. EngagementTemplateType lives in guest-engagement.types
 * and is intentionally not redefined here.
 */

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export enum ChargeTypeCategory {
  ROOM = 'room',
  FOOD = 'food',
  TAX = 'tax',
  SERVICE = 'service',
  MISC = 'misc',
}

export enum PromotionDiscountType {
  PERCENTAGE = 'percentage',
  FIXED = 'fixed',
}

export enum CancelReasonCategory {
  GUEST = 'guest',
  HOTEL = 'hotel',
  NO_SHOW = 'no_show',
  OTA = 'ota',
}

export enum DepositBasis {
  PERCENT_OF_STAY = 'percent_of_stay',
  FIXED = 'fixed',
  FIRST_NIGHT = 'first_night',
}

export enum DepositDueTiming {
  AT_BOOKING = 'at_booking',
  DAYS_BEFORE_ARRIVAL = 'days_before_arrival',
}

export enum PackageElementType {
  INCLUSION = 'inclusion',
  SERVICE = 'service',
  FNB = 'fnb',
}

export enum PackageElementPer {
  STAY = 'stay',
  NIGHT = 'night',
  PERSON = 'person',
}

// ---------------------------------------------------------------------------
// Flow 1 — Charge Types
// ---------------------------------------------------------------------------

export interface ChargeType {
  id: string;
  tenantId: string;
  propertyId: string;
  name: string;
  code: string;
  category: ChargeTypeCategory;
  defaultAmount: string | null;
  taxable: boolean;
  postingCode: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Flow 2 — Promotion Codes
// ---------------------------------------------------------------------------

export interface Promotion {
  id: string;
  tenantId: string;
  propertyId: string;
  code: string;
  description: string | null;
  discountType: PromotionDiscountType;
  value: string;
  validFrom: string;
  validTo: string;
  minNights: number | null;
  minSpend: string | null;
  maxRedemptions: number | null;
  redemptionCount: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Flow 3 — Cancellation Reason Codes
// ---------------------------------------------------------------------------

export interface CancellationReasonCode {
  id: string;
  tenantId: string;
  propertyId: string;
  label: string;
  code: string;
  category: CancelReasonCategory;
  requiresNote: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Flow 4 — Deposit Policies
// ---------------------------------------------------------------------------

export interface DepositPolicy {
  id: string;
  tenantId: string;
  propertyId: string;
  name: string;
  code: string;
  basis: DepositBasis;
  value: string | null;
  dueTiming: DepositDueTiming;
  daysBeforeArrival: number | null;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Flow 5 — Rate Plan Packages (header + elements)
// ---------------------------------------------------------------------------

export interface Package {
  id: string;
  tenantId: string;
  propertyId: string;
  name: string;
  code: string;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PackageElement {
  id: string;
  packageId: string;
  tenantId: string;
  name: string;
  elementType: PackageElementType;
  amount: string;
  per: PackageElementPer;
  createdAt: string;
}

export interface PackageWithCount extends Package {
  elementsCount: number;
  /** Computed sum of element amounts (DECIMAL string). */
  totalValue: string;
}

// ---------------------------------------------------------------------------
// Flow 6 — Housekeeping Service Types
// ---------------------------------------------------------------------------

export interface HousekeepingServiceType {
  id: string;
  tenantId: string;
  propertyId: string;
  name: string;
  code: string;
  estimatedMinutes: number | null;
  color: string | null;
  defaultFrequency: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Flow 7 — Online Check-Out settings
// ---------------------------------------------------------------------------

export interface OnlineCheckoutSettings {
  id: string;
  tenantId: string;
  propertyId: string;
  isEnabled: boolean;
  windowHoursBefore: number | null;
  windowHoursAfter: number | null;
  requireFolioSettled: boolean;
  confirmationMessage: string | null;
  createdAt: string;
  updatedAt: string;
}
